import React, { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, Users, Send } from 'lucide-react';
import templateService from '../../../services/whatsapp/templateService';
import contactService from '../../../services/whatsapp/contactService';
import broadcastService from '../../../services/whatsapp/broadcastService';
import {
  Contact,
  Template,
  extractTemplateVariables
} from '../../../types/models/whatsapp';

interface NewCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/** Sources offered per template variable. */
const VARIABLE_SOURCES = [
  { value: 'contact.name', label: "Contact's name" },
  { value: 'contact.phone', label: "Contact's phone" },
  { value: '__literal__', label: 'Fixed text…' }
];

/**
 * Campaign wizard.
 *
 * Only APPROVED templates are offered, and the audience is previewed before
 * anything can be started — a broadcast is expensive and irreversible, so the
 * recipient count must be visible before the send button is reachable.
 */
const NewCampaignDialog: React.FC<NewCampaignDialogProps> = ({
  open,
  onClose,
  onCreated
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [audienceTags, setAudienceTags] = useState<string[]>([]);
  const [optInFilter, setOptInFilter] = useState<'opted_in' | 'any'>('opted_in');
  const [throttle, setThrottle] = useState(10);
  const [trackedUrl, setTrackedUrl] = useState('');
  const [variableSources, setVariableSources] = useState<Record<string, string>>({});
  const [literals, setLiterals] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setName('');
    setDescription('');
    setTemplateName('');
    setAudienceTags([]);
    setOptInFilter('opted_in');
    setThrottle(10);
    setTrackedUrl('');
    setVariableSources({});
    setLiterals({});
    setError(null);

    templateService
      .listSendable()
      .then(setTemplates)
      .catch((err) => setError(err.message || 'Could not load templates'));

    const unsubscribe = contactService.subscribe(setContacts);
    return unsubscribe;
  }, [open]);

  const template = useMemo(
    () => templates.find((t) => t.name === templateName) || null,
    [templates, templateName]
  );

  const variables = useMemo(
    () => (template ? extractTemplateVariables(template) : []),
    [template]
  );

  /**
   * Link tracking only applies when the approved template actually has a URL
   * button — otherwise there is nowhere to put the redirect token.
   */
  const hasUrlButton = useMemo(
    () =>
      (template?.components || []).some(
        (c) => c.type === 'BUTTONS' && (c.buttons || []).some((b) => b.type === 'URL')
      ),
    [template]
  );

  const allTags = useMemo(() => {
    const seen = new Set<string>();
    contacts.forEach((c) => c.tags.forEach((tag) => seen.add(tag)));
    return Array.from(seen).sort();
  }, [contacts]);

  /**
   * Live audience preview. Mirrors the server's resolution: every selected tag
   * must be present, opted-out and blocked contacts are excluded from the
   * sendable count.
   */
  const audience = useMemo(() => {
    let matched = contacts;

    if (audienceTags.length > 0) {
      matched = matched.filter((c) =>
        audienceTags.every((tag) => c.tags.includes(tag))
      );
    }

    if (optInFilter === 'opted_in') {
      matched = matched.filter((c) => c.optIn.status === 'opted_in');
    }

    const sendable = matched.filter(
      (c) => !c.blocked && c.optIn.status !== 'opted_out'
    );

    return { matched: matched.length, sendable: sendable.length };
  }, [contacts, audienceTags, optInFilter]);

  if (!open) return null;

  const handleCreate = async (andStart: boolean) => {
    if (!name.trim()) {
      setError('Give the campaign a name');
      return;
    }
    if (!template) {
      setError('Choose an approved template');
      return;
    }
    if (audience.sendable === 0) {
      setError('This audience has no contacts that can be messaged');
      return;
    }

    const variableMapping: Record<string, string> = {};
    variables.forEach((position) => {
      const source = variableSources[position] || 'contact.name';
      variableMapping[position] =
        source === '__literal__' ? literals[position] || '' : source;
    });

    setSaving(true);
    setError(null);
    try {
      const { id } = await broadcastService.create({
        name: name.trim(),
        description: description.trim(),
        templateName: template.name,
        templateLanguage: template.language,
        variableMapping,
        audience: {
          tags: audienceTags.length > 0 ? audienceTags : undefined,
          optInStatus: optInFilter === 'opted_in' ? 'opted_in' : undefined
        },
        throttlePerSecond: throttle,
        trackedUrl: hasUrlButton && trackedUrl.trim() ? trackedUrl.trim() : null
      });

      if (andStart) {
        await broadcastService.start(id);
      } else {
        // Resolve recipients so the campaign shows a real Total while in draft.
        await broadcastService.prepare(id);
      }

      onCreated(id);
    } catch (err: any) {
      setError(err.message || 'Could not create the campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            New campaign
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-700">
                Campaign name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Football trials — Noida"
                className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-700">
                Description (optional)
              </span>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Template (approved only)
            </span>
            <select
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Choose a template…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name} ({t.language}) · {t.category}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <span className="mt-1 block text-[11px] text-warning-700">
                No approved templates found. Sync templates from the inbox first.
              </span>
            )}
          </label>

          {template && (
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Preview
              </p>
              <p className="mt-1 whitespace-pre-wrap text-xs text-gray-700">
                {template.components.find((c) => c.type === 'BODY')?.text || ''}
              </p>
            </div>
          )}

          {variables.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">
                Variables
              </p>
              {variables.map((position) => (
                <div key={position} className="flex items-center gap-2">
                  <span className="w-12 shrink-0 font-mono text-xs text-gray-500">
                    {`{{${position}}}`}
                  </span>
                  <select
                    value={variableSources[position] || 'contact.name'}
                    onChange={(e) =>
                      setVariableSources({
                        ...variableSources,
                        [position]: e.target.value
                      })
                    }
                    className="min-w-0 flex-1 rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
                  >
                    {VARIABLE_SOURCES.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                  {variableSources[position] === '__literal__' && (
                    <input
                      type="text"
                      value={literals[position] || ''}
                      onChange={(e) =>
                        setLiterals({ ...literals, [position]: e.target.value })
                      }
                      placeholder="Fixed value"
                      className="min-w-0 flex-1 rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-gray-700">
              Audience
            </p>

            <div className="mt-1.5 flex flex-wrap gap-1">
              {allTags.length === 0 && (
                <span className="text-xs text-gray-400">
                  No tags yet — the campaign will target all contacts.
                </span>
              )}
              {allTags.map((tag) => {
                const active = audienceTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() =>
                      setAudienceTags(
                        active
                          ? audienceTags.filter((t) => t !== tag)
                          : [...audienceTags, tag]
                      )
                    }
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>

            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={optInFilter === 'opted_in'}
                onChange={(e) =>
                  setOptInFilter(e.target.checked ? 'opted_in' : 'any')
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-xs text-gray-700">
                Only contacts who have explicitly opted in (recommended)
              </span>
            </label>

            <div className="mt-2 flex items-center gap-2 rounded-lg bg-primary-50 p-2 text-xs text-primary-700">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span>
                <strong>{audience.sendable}</strong> contact
                {audience.sendable === 1 ? '' : 's'} will receive this
                {audience.matched !== audience.sendable && (
                  <>
                    {' '}
                    ({audience.matched - audience.sendable} excluded as blocked or
                    opted out)
                  </>
                )}
              </span>
            </div>

            {optInFilter === 'any' && (
              <p className="mt-2 flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-warning-700">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                Including contacts with unknown consent risks spam reports, which
                lower your number's quality rating and can get it restricted.
              </p>
            )}
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Send rate: {throttle}/second
            </span>
            <input
              type="range"
              min={1}
              max={50}
              value={throttle}
              onChange={(e) => setThrottle(Number(e.target.value))}
              className="mt-1 w-full"
            />
            <span className="text-[11px] text-gray-500">
              Lower rates are gentler on your messaging tier.
            </span>
          </label>

          {hasUrlButton && (
            <label className="block">
              <span className="text-xs font-medium text-gray-700">
                Tracked link destination (optional)
              </span>
              <input
                type="url"
                value={trackedUrl}
                onChange={(e) => setTrackedUrl(e.target.value)}
                placeholder="https://amaplayer.com/trials"
                className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <span className="text-[11px] text-gray-500">
                This template has a URL button. Set a destination to count
                clicks — each recipient gets a unique redirect, which is the
                only way Meta lets you measure them.
              </span>
            </label>
          )}

          {error && <p className="text-xs text-danger-600">{error}</p>}
        </div>

        <div className="flex gap-2 border-t border-gray-200 p-3">
          <button
            type="button"
            onClick={() => handleCreate(false)}
            disabled={saving}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save as draft'}
          </button>
          <button
            type="button"
            onClick={() => handleCreate(true)}
            disabled={saving || audience.sendable === 0}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-wa-green px-3 py-2 text-sm font-medium text-white hover:bg-wa-teal disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {saving ? 'Starting…' : `Send to ${audience.sendable}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewCampaignDialog;
